import { useEffect, useRef } from 'react';
import { format, parseISO } from 'date-fns';
import { type Locale, ca, es, fr, it, enUS } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import type { ItineraryDay } from '../../../types';
import type { DayWeather } from '../../../api/weather.api';
import { localDateKey } from '../../../utils/dateTime';
import { legStartingOn, shortName } from '../../../utils/tripLegs';
import { DayWeatherChip } from './DayWeatherChip';

const LOCALES: Record<string, Locale> = { ca, es, fr, it };

/** A day can arrive with a `place` and no numbers; then the chip renders nothing. */
const hasReading = (w?: DayWeather) => !!w && (w.tempMax !== null || w.tempMin !== null);

interface Props {
  days: ItineraryDay[];
  activeId: string | null;
  onSelect: (day: ItineraryDay) => void;
  /** Keyed by `YYYY-MM-DD`; empty while it loads or when there is no provider. */
  weather?: Record<string, DayWeather>;
  /**
   * Holds the chip's line open with a placeholder while the request is in
   * flight. Without it the whole strip grows under the pointer the moment the
   * weather lands — and it lands late, because it is a separate request.
   */
  weatherLoading?: boolean;
  /**
   * The trip's destinations (ADR-0030). With two or more, the first day of each
   * one carries its name above the chip.
   */
  legs?: readonly { name: string; nights: number }[];
  /** Injectable so the marker can be tested without moving the system clock. */
  todayKey?: string;
}

export function DayStrip({
  days, activeId, onSelect, weather = {}, weatherLoading = false, legs = [], todayKey = localDateKey(),
}: Props) {
  const { t, i18n } = useTranslation();
  const locale = LOCALES[i18n.language] ?? enUS;
  const scrollerRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLDivElement>(null);

  // The strip scrolls sideways, so a trip that opens on day 10 would select a
  // chip nobody can see. Driving the scroller by hand rather than
  // `scrollIntoView`: that one can also drag the *page* down to reach the strip.
  useEffect(() => {
    const scroller = scrollerRef.current;
    const chip = activeRef.current;
    if (!scroller || !chip) return;
    const centred = chip.offsetLeft - (scroller.clientWidth - chip.clientWidth) / 2;
    scroller.scrollTo({ left: Math.max(0, centred), behavior: 'smooth' });
  }, [activeId]);

  // Once loaded, the line stays reserved on every day as long as *some* day has
  // a reading, so a day the provider could not answer for doesn't sit lower
  // than its neighbours. A trip with no weather at all keeps the short button.
  const reserveWeatherLine = weatherLoading || days.some(d => hasReading(weather[d.date.slice(0, 10)]));
  const showLegs = legs.length > 1;

  return (
    <div ref={scrollerRef} className="flex gap-1.5 overflow-x-auto scrollbar-none pb-1 -mb-1">
      {days.map((day, i) => {
        const dateKey = day.date.slice(0, 10);
        const date = parseISO(dateKey);
        const isActive = day.id === activeId;
        const isToday = dateKey === todayKey;
        const dayWeather = weather[dateKey];
        const legStart = showLegs ? legStartingOn(legs, day.dayNumber) : null;
        return (
          <div
            key={day.id}
            ref={isActive ? activeRef : undefined}
            className={`flex flex-col gap-1 shrink-0 ${legStart && i > 0 ? 'ml-3' : ''}`}
          >
            {showLegs && (
              /* Every chip keeps the line so the row stays level; the name overflows
                 to the right, over the next chips' empty lines. */
              <div className="relative h-4">
                {legStart && (
                  <span className="absolute left-0 top-0 whitespace-nowrap border-l-2 border-teal-400 pl-1.5 text-[11px] font-semibold leading-4 text-slate-500 dark:text-slate-400">
                    {shortName(legStart.name)}
                  </span>
                )}
              </div>
            )}
            <button
              onClick={() => onSelect(day)}
              /* The itinerary opens on today by itself, so today has to be
                 readable as today — otherwise landing on day 4 looks like the app
                 remembered something. The ring survives selecting another day,
                 which is also how you find your way back. */
              title={isToday ? t('common.today') : undefined}
              className={`flex flex-col items-center justify-center min-w-[62px] min-h-[60px] py-2 px-2 rounded-xl transition-all cursor-pointer shrink-0 ${
                isActive
                  ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-100 hover:bg-slate-200 dark:hover:bg-slate-700'
              } ${
                /* Inset: the strip is an overflow-x container, so a ring drawn
                   outside the chip gets clipped on the top edge and at both ends. */
                isToday ? 'ring-2 ring-inset ring-teal-400' : ''
              }`}
            >
              {isToday && <span className="sr-only">{t('common.today')}</span>}
              {/* The big number is the calendar date; which day of the trip it is
                  is the other half of the question, and only the timeline view
                  used to answer it. The separator is a margin, not two spaces:
                  at this size a space each side is ~12px of button width. `min-w`
                  covers a two-digit index so a trip that reaches day 10 keeps all
                  its buttons the same width. */}
              <span className="font-mono text-[9px] tracking-[0.08em] uppercase leading-none mb-1 whitespace-nowrap">
                {t('itinerary.dayShort', { n: day.dayNumber })}
                <span className="opacity-40 mx-[1.5px]">·</span>
                {format(date, 'EEE', { locale })}
              </span>
              <span className="font-mono font-bold text-[22px] leading-none tabular-nums">
                {format(date, 'd')}
              </span>
              {day.activities.length > 0 && (
                <div className={`w-1 h-1 rounded-full mt-1 ${isActive ? 'bg-white/60 dark:bg-slate-900/60' : 'bg-teal-400'}`} />
              )}
              {reserveWeatherLine && (
                <div className="mt-1 h-[11px] flex items-center justify-center">
                  {hasReading(dayWeather)
                    ? <DayWeatherChip weather={dayWeather} locale={locale} />
                    : weatherLoading && (
                        <span
                          aria-hidden
                          className={`block h-[8px] w-8 rounded animate-pulse ${
                            isActive ? 'bg-white/25 dark:bg-slate-900/25' : 'bg-slate-300/70 dark:bg-slate-700'
                          }`}
                        />
                      )}
                </div>
              )}
            </button>
          </div>
        );
      })}
    </div>
  );
}
